/** @format */

import { memo } from 'react'

import styles from 'dir/styles/courses.module.css'
import counterClosureImg from 'assets/images/counter-closure.png'
import shelfClosureImg from 'assets/images/shef-closure.png'
import { CopyBlock, ocean } from 'react-code-blocks'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Замыкания</h2>
    <p className={styles.description}>
      <b>Замыкание (closure)</b> — это связь между функцией и ее цепочкой областей видимости.
    </p>
    <p className={styles.description}>
      Этот механизм возможен благодаря лексическому окружению. При вызове функции, ее лексическое окружение получает
      ссылку на ту область видимости, где функция была объявлена (внешнее лексическое окружение), тем самым получая
      доступ к переменным из нее, буквально удерживая в памяти таблицу доступных переменных.
    </p>
    <p className={styles.description}>
      Итого, замыкание - это термин который описывает способность функции запоминать лексическое окружение в котором она
      была объявлена, и продолжать получать доступ к переменным из этого лексического окружения вне зависимости от того,
      где она была вызвана.
    </p>
    <p className={styles.description}>Рассмотрим пример функции-счетчика с недоступной извне переменной.</p>
    <CopyBlock
      text={`const createCounter = function () {
  /*
   * Локальная переменная privateValue доступна только в замыкании.
   * Получить к ней доступ во внешнем коде невозможно.
   */
  let privateValue = 0;

  const increment = function () {
    privateValue += 1;
    console.log(privateValue);
  };

  return {
    increment,
  };
};

const counterA = createCounter();
counterA.increment(); // 1
counterA.increment(); // 2

const counterB = createCounter();
counterB.increment(); // 1
counterB.increment(); // 2
counterB.increment(); // 3`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      В примере функция <code className={styles.code}>createCounter</code> создаёт локальную переменную{' '}
      <code className={styles.code}>privateValue</code>, а также локальную функцию{' '}
      <code className={styles.code}>increment</code>, которая увеличивает значение{' '}
      <code className={styles.code}>privateValue</code>и выводит значение в консоль.
    </p>
    <p className={styles.description}>
      Функция <code className={styles.code}>increment</code> не содержит собственной локальной переменной{' '}
      <code className={styles.code}>privateValue</code>, но благодаря механизму замыкания ей доступна переменная из
      внешнего окружения функции <code className={styles.code}>createCounter</code>. В результате она может пользоваться
      переменной
      <code className={styles.code}>privateValue</code>, созданной во время вызова функции{' '}
      <code className={styles.code}>createCounter</code>, даже после того, как{' '}
      <code className={styles.code}>increment</code> будет возвращена из вызова{' '}
      <code className={styles.code}>createCounter</code>.
    </p>
    <p className={styles.description}>
      Если заглянуть внутрь <code className={styles.code}>counterA</code> и{' '}
      <code className={styles.code}>counterB</code> используя <code className={styles.code}>console.dir()</code>,
      наглядно видно механизм замыкания.
    </p>
    <img className={styles.img} src={counterClosureImg} alt="counterClosureImg" />
    <p className={styles.description}>Напишем функцию приготовления поваром некого блюда.</p>
    <CopyBlock
      text={`const makeDish = function (shefName, dish) {
  console.log(\`\${shefName} is cooking \${dish}\`);
};

makeDish('Mango', 'apple pie'); // Mango is cooking apple pie
makeDish('Mango', 'fish'); // Mango is cooking fish
makeDish('Mango', 'beef stew'); // Mango is cooking beef stew

makeDish('Poly', 'muffins'); // Poly is cooking muffins
makeDish('Poly', 'pork chops'); // Poly is cooking pork chops
makeDish('Poly', 'roast beef'); // Poly is cooking roast beef`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Проблема такого подхода в том, что при каждом вызове функции приходится передавать имя повара который будет
      готовить блюдо. Решить эту проблему можно используя замыкание. Пишем функцию создания шефа с именем, которая
      возвращает другую функцию для приготовления блюда.
    </p>
    <CopyBlock
      text={`const makeShef = function (name) {
  /*
   * Параметр name это локальная переменная для функции makeShef.
   * Это значит что она будет доступна функции makeDish через замыкание.
   */
  return function makeDish(dish) {
    console.log(\`\${name} is cooking \${dish}\`);
  };
};

const mango = makeShef('Mango');
mango('apple pie'); // Mango is cooking apple pie
mango('beef stew'); // Mango is cooking beef stew

const poly = makeShef('Poly');
poly('pancakes'); // Poly is cooking pancakes
poly('eggs and bacon'); // Poly is cooking eggs and bacon`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>Посмотрим в консоль.</p>
    <img className={styles.img} src={shelfClosureImg} alt="shelfClosureImg" />

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Дополнительные материалы</h2>
      <p className={styles.description}>
        Исчерпывающая статья по замыканиям от <b>Preethi Kasireddy</b>, по ссылкам ниже, обязательна к прочтению в
        свободное от изучения основного материала время.
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://medium.com/@sshambir/%D0%BE%D1%81%D0%B2%D0%B0%D0%B8%D0%B2%D0%B0%D0%B5%D0%BC-%D0%B7%D0%B0%D0%BC%D1%8B%D0%BA%D0%B0%D0%BD%D0%B8%D1%8F-%D0%B2-javascript-5b83267ef7d1"
            target="_blank"
          >
            Осваиваем замыкания в Javascript (перевод ru)
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            rel="noopener noreferrer"
            href="https://medium.freecodecamp.org/lets-learn-javascript-closures-66feb44f6a44"
            target="_blank"
          >
            Let’s Learn JavaScript Closures (оригинал en)
          </a>
        </li>
      </ul>
    </section>
  </section>
))
